import { useLayoutEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useChatPresence } from "@/hooks/useChatPresence";

import { FloatingChatSurface } from "@/components/FloatingChat/FloatingChatSurface";
import {
  FLOATING_CHAT_MIN_HEIGHT_PX,
  FLOATING_CHAT_MIN_WIDTH_PX,
} from "@/components/FloatingChat/floatingChatShell";
import { useAnchorRect } from "@/components/FloatingChat/useAnchorRect";
import { useRegionMaxPanelHeight } from "@/components/FloatingChat/useRegionMaxPanelHeight";
import { useViewportSize } from "@/components/FloatingChat/useViewportSize";
import { usePaneLayoutWidths } from "@/context/PaneLayoutWidthsContext";
import { useRightPaneExpandContext } from "@/context/RightPaneExpandContext";
import { useComposerProgress } from "@/hooks/useComposerProgress";
import { useWorkspaceLeftRailLayoutWidth } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/WorkspaceLeftRail";
import { usePresentation } from "@/onboarding";
import type { LocationState } from "@/types/pane-routing";
import { useStore } from "@xyflow/react";
import {
  CANVAS_FLOATING_CHAT_TOOLBAR_RIGHT_MARGIN,
  CANVAS_TOP_CONTROLS_BAND_BOTTOM_PX,
  useCanvasFloatingChat,
} from "../context/CanvasFloatingChatContext";
import { useCanvasRoute } from "../context/CanvasRouteContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useCanvasChatReferencedEntities } from "../hooks/useCanvasChatReferencedEntities";
import { useCanvasWidth } from "../hooks/useCanvasWidth";
import { CanvasSessionPaneContent } from "./CanvasSessionPane";
import { CanvasChatFab } from "./composer/CanvasChatFab";
import { CanvasLanding } from "./composer/CanvasLanding";

/** A window shorter than this would hide the composer behind the header. */
const FLOATING_CHAT_FIT_MIN_HEIGHT_PX = 240;

/** FAB diameter (`size-14`) — the toolbar only needs to clear this while closed. */
const FAB_FOOTPRINT_PX = 56;

/**
 * Horizontal clearance vs the React Flow wrapper width: matches shell `right-2`
 * (8px) plus the same gutter on the left when the panel is dragged to max width.
 */
const FLOATING_CHAT_HORIZONTAL_INSET_PX = 16;

/** Extra horizontal clearance when only the right pane is open (toolbar / shell margin). */
const FLOATING_CHAT_RIGHT_DOCK_ONLY_MARGIN_PX = 100;

/** Matches `@max-[399px]:fixed` on the shell (container query on React Flow wrapper). */
const CANVAS_FLOATING_CHAT_FIXED_MODE_MAX_CONTAINER_PX = 399;

function chatShellOverlapsTopControls(
  chatShell: HTMLElement,
  flowWrapper: HTMLDivElement | null,
): boolean {
  if (!flowWrapper) return false;
  const chatTop = chatShell.getBoundingClientRect().top;
  const controlsBottom =
    flowWrapper.getBoundingClientRect().top +
    CANVAS_TOP_CONTROLS_BAND_BOTTOM_PX;
  return chatTop <= controlsBottom;
}

export function CanvasFloatingChatPanel() {
  const { canvasId } = useCanvasRoute();
  const canvasWidthPx = useCanvasWidth();
  const { reactFlowWrapperEl } = useCanvasUi();
  // Node COUNT only (selector) — so node drags/streams don't re-render the chat window.
  const nodeCount = useStore((s) => s.nodes.length);
  const canvasChatHidden = usePresentation("canvasChatHidden");
  const {
    currentSessionId,
    canGoBack,
    goBack,
    openSession,
    setBottomToolbarRightClearancePx,
    setTopControlsRightClearancePx,
    setIsChatResizing,
    open,
    expand,
    collapse,
    sizeClass,
    toggleSizeClass,
    activeSessionId,
    hasStarted,
  } = useCanvasFloatingChat();
  const expandedShellRef = useRef<HTMLDivElement>(null);
  // Floored below the usual minimum so a short window shrinks the chat to fit
  // instead of pushing its composer off the bottom of the screen.
  const flowShellMaxHeightPx = useRegionMaxPanelHeight(
    reactFlowWrapperEl,
    FLOATING_CHAT_FIT_MIN_HEIGHT_PX,
  );

  // Shared chat data (single source for the floating overlay AND the input). Canvas-scoped,
  // so it's safe at the panel level (no SessionProvider needed).
  const { referencedEntities, onRemoveEntity, clearTextSelectionsFromMessage } =
    useCanvasChatReferencedEntities(canvasId ?? "");
  const progress = useComposerProgress(currentSessionId ?? activeSessionId);

  const isFloatingChatFixedToViewport =
    canvasWidthPx > 0 &&
    canvasWidthPx <= CANVAS_FLOATING_CHAT_FIXED_MODE_MAX_CONTAINER_PX;

  const { viewportWidthPx, viewportHeightPx } = useViewportSize();

  // Geometry of the canvas content area. Its left/top/height stay valid even
  // when the React Flow wrapper is squeezed to ~0 width by a full-width detail
  // pane — so the left-docked chat / FAB can anchor to the content area (after
  // the left explorer, below any chrome) without hard-coding a header offset.
  // Rail width as the anchor's remeasure key — the same key the workspace
  // panel already passes: the rail opening/closing/resizing moves the region
  // without resizing it when the region's width is pinned, which a
  // ResizeObserver cannot see. Without the key, the left dock's fixed `left`
  // goes stale and the dock sits under the rail.
  const railLayoutWidth = useWorkspaceLeftRailLayoutWidth();
  const wrapperRect = useAnchorRect(reactFlowWrapperEl, railLayoutWidth);

  const { rightPanelWidth } = usePaneLayoutWidths();
  const { isExpanded: isRightPanelExpanded } = useRightPaneExpandContext();
  const location = useLocation();

  const isRightPanelOpenByRoute = useMemo(() => {
    const state = (location.state as LocationState) || {};
    return Boolean(state.rightPane?.pathname);
  }, [location.state]);

  // ── Surface state ─────────────────────────────────────────────────────────
  // open = the chat window is up; otherwise the FAB launcher (or the centered
  // landing on a fresh canvas). `full-height` docks the window flush-right.
  const isOpen = open && !!currentSessionId;

  // Report presence for the canvas chat so the backend suppresses
  // $toolConfirmation notifications while the user is actively watching it. Only
  // while the window is open — collapsed to the FAB reports no view, so a
  // confirmation the user can't see still surfaces in the bell. Quick-chat nodes
  // are intentionally excluded: several can be open at once with no single focus,
  // which the one-session-per-user presence model can't represent.
  useChatPresence(isOpen ? (currentSessionId ?? undefined) : undefined);

  const isFullHeight = sizeClass === "full-height";
  const showLanding = !hasStarted && nodeCount === 0;
  // During onboarding the coach card owns the bottom of the screen. The store
  // lifts the field for the steps that teach "open chat" on the FAB, so the
  // launcher is there when they spotlight it.
  const onboardingSuppressed = canvasChatHidden && !isOpen;

  // When the detail pane is expanded full-width it occupies the right, so the
  // chat moves to the LEFT: the FAB and the windowed window float over the
  // detail; full-height instead docks as a resizable column and the detail
  // shrinks to sit beside it (two-pane) — `FloatingChatLeftDock` reserves
  // `leftChatDockPx` itself from its live width.
  const dockLeft = isRightPanelExpanded && isRightPanelOpenByRoute;

  // Windowed chat floating over a full-width detail: keep it from covering the
  // whole pane.
  const leftWindowMaxWidthPx = Math.max(400, Math.floor(viewportWidthPx * 0.5));

  const maxWidthPx = useMemo(() => {
    const viewport = viewportWidthPx;
    const gutterPx = FLOATING_CHAT_HORIZONTAL_INSET_PX;
    const rightOpen = isRightPanelOpenByRoute;
    const rightPx = rightOpen ? rightPanelWidth : 0;

    // Every cap subtracts the rail: the caps were viewport-based, so a chat
    // anchored to the right edge could grow leftward under the rail (and the
    // below-400px fixed-escape mode had no left bound at all). The rail edge
    // is the hard left wall for every chat mode.
    const railPx = railLayoutWidth;
    let rawMax: number;

    if (rightOpen && isRightPanelExpanded) {
      rawMax = viewport - railPx;
    } else if (
      rightOpen &&
      !isRightPanelExpanded &&
      isFloatingChatFixedToViewport
    ) {
      rawMax = viewport - railPx - gutterPx;
    } else if (rightOpen && !isRightPanelExpanded) {
      rawMax =
        viewport - railPx - rightPx - FLOATING_CHAT_RIGHT_DOCK_ONLY_MARGIN_PX;
    } else if (isFloatingChatFixedToViewport) {
      rawMax = viewport - railPx;
    } else {
      rawMax = Math.min(
        Math.floor(viewport * 0.6),
        viewport - railPx - gutterPx,
      );
    }

    return Math.max(FLOATING_CHAT_MIN_WIDTH_PX, rawMax);
  }, [
    isFloatingChatFixedToViewport,
    isRightPanelExpanded,
    isRightPanelOpenByRoute,
    railLayoutWidth,
    rightPanelWidth,
    viewportWidthPx,
  ]);

  const { initialHeight, minHeight } = useMemo(() => {
    if (typeof window === "undefined") {
      return { initialHeight: 520, minHeight: FLOATING_CHAT_MIN_HEIGHT_PX };
    }
    const cap = flowShellMaxHeightPx;
    const minHeight = Math.min(FLOATING_CHAT_MIN_HEIGHT_PX, cap);
    return {
      minHeight,
      initialHeight: Math.max(minHeight, Math.min(560, cap)),
    };
  }, [flowShellMaxHeightPx]);

  // Toolbar clearance: keep the bottom canvas toolbar left of the chat. While closed it only
  // needs to clear the FAB footprint; while open the window is observed (resizable). When the
  // shell grows upward (full-height dock or manual vertical resize), also offset the top-right
  // share + controls row. Landing is centered and onboarding-suppressed state has no shell.
  useLayoutEffect(() => {
    const resetClearances = () => {
      setBottomToolbarRightClearancePx(0);
      setTopControlsRightClearancePx(0);
    };

    if (!canvasId || onboardingSuppressed || showLanding) {
      resetClearances();
      return;
    }
    // Detail pane full-width: the chat/FAB live on the LEFT, so the bottom
    // toolbar needs no right clearance for them.
    if (dockLeft) {
      resetClearances();
      return;
    }

    const applyClearances = (width: number, overlapsTopControls: boolean) => {
      const bottomClearance =
        Math.ceil(width) + CANVAS_FLOATING_CHAT_TOOLBAR_RIGHT_MARGIN;
      setBottomToolbarRightClearancePx(bottomClearance);
      setTopControlsRightClearancePx(overlapsTopControls ? bottomClearance : 0);
    };

    if (!isOpen) {
      applyClearances(FAB_FOOTPRINT_PX, false);
      return;
    }

    let ro: ResizeObserver | undefined;
    let measure: (() => void) | undefined;
    let rafId = 0;
    let cancelled = false;

    const cleanup = () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      ro?.disconnect();
      if (measure) {
        window.removeEventListener("resize", measure);
      }
    };

    const attach = (el: HTMLDivElement) => {
      measure = () => {
        const rect = el.getBoundingClientRect();
        const overlapsTopControls =
          isFullHeight || chatShellOverlapsTopControls(el, reactFlowWrapperEl);
        applyClearances(rect.width, overlapsTopControls);
      };

      ro = new ResizeObserver(() => {
        measure?.();
      });
      ro.observe(el);
      if (reactFlowWrapperEl) {
        ro.observe(reactFlowWrapperEl);
      }
      window.addEventListener("resize", measure);
      measure();
    };

    const tryAttach = () => {
      if (cancelled) return;
      const el = expandedShellRef.current;
      if (el) {
        attach(el);
        return;
      }
      // AnimatePresence / motion can commit the shell one frame after isOpen flips.
      rafId = requestAnimationFrame(tryAttach);
    };

    tryAttach();
    return cleanup;
  }, [
    canvasId,
    isOpen,
    isFullHeight,
    showLanding,
    onboardingSuppressed,
    dockLeft,
    reactFlowWrapperEl,
    setBottomToolbarRightClearancePx,
    setTopControlsRightClearancePx,
  ]);

  // The window does NOT auto-minimize — minimizing to the FAB is an explicit action (header Minus).

  // Closed-state FAB open (activity pulse + attached-entity count badge).
  // A tour that spotlights the launcher takes the click before this runs and
  // opens its own session instead.
  const handleFabOpen = () => {
    expand();
  };

  // ── Render (all hooks above) ──────────────────────────────────────────────
  if (!canvasId) return null;
  if (onboardingSuppressed) return null;
  if (showLanding) return <CanvasLanding />;

  return (
    <FloatingChatSurface
      isOpen={isOpen}
      dockLeft={dockLeft}
      sizeClass={sizeClass}
      anchorRect={wrapperRect}
      viewportHeightPx={viewportHeightPx}
      storageKey="canvas-floating-chat-width"
      shellRef={expandedShellRef}
      // React Flow shell is `@container` on CanvasPage; below 400px the corner
      // shell pins to the viewport so the chat clears the right pane.
      escapeToViewportWhenNarrow
      windowMaxWidthPx={maxWidthPx}
      initialHeightPx={initialHeight}
      minHeightPx={minHeight}
      maxHeightPx={flowShellMaxHeightPx}
      leftWindowMaxWidthPx={leftWindowMaxWidthPx}
      onResizeStart={() => setIsChatResizing(true)}
      onResizeEnd={() => setIsChatResizing(false)}
      renderPane={({ isChatExpanded }) => (
        <CanvasSessionPaneContent
          canvasId={canvasId}
          flowSessionId={currentSessionId ?? ""}
          canGoBack={canGoBack}
          onGoBack={goBack}
          onClose={collapse}
          isChatExpanded={isChatExpanded}
          onToggleExpand={toggleSizeClass}
          onOpenSession={openSession}
          referencedEntities={referencedEntities}
          onRemoveEntity={onRemoveEntity}
          clearTextSelectionsFromMessage={clearTextSelectionsFromMessage}
        />
      )}
      renderFab={(dockStyle) => (
        // While a full-width detail pane occupies the right, the FAB pins to
        // the bottom-LEFT of the canvas content area instead of its shell
        // bottom-right (`dockStyle`).
        <CanvasChatFab
          onOpen={handleFabOpen}
          running={progress.isRunning}
          dockStyle={dockStyle}
        />
      )}
    />
  );
}
